import { getLogger } from '../../diagnostics/logger';
import * as oidc from 'oidc-client-ts';
import * as React from 'react';
import { useEffect } from 'react';
import { Store } from 'redux';
import config from '../../config';
import { clearAuthenticatedUser, setAuthenticatedUser } from '../../diagnostics/applicationInsights';

const log = getLogger('simManagement.api.components.login.OidcProvider');

import {
  accessTokenExpired,
  accessTokenExpiring,
  silentRenewError,
  userLoaded,
  userSignedOut,
  userUnloaded,
} from '../../actions/loginActions';

interface Props {
  userManager: oidc.UserManager;
  store: Store<any, any>;
  children?: React.ReactNode;
}

export function OidcProvider({ userManager, store, children }: Props) {
  useEffect(() => {
    const isLocal = config.isLocal && config.isLocal();

    const onUserLoaded = (user: oidc.User) => {
      setAuthenticatedUser(user);
      store.dispatch(userLoaded(user));
    };

    const onSilentRenewError = (error: any) => {
      store.dispatch(silentRenewError(error));
    };

    const onAccessTokenExpired = () => {
      log.warn('Got "access token expired" event from user manager.');
      store.dispatch(accessTokenExpired());
    };

    const onAccessTokenExpiring = () => {
      log.warn('Got "access token expiring" event from user manager.');
      store.dispatch(accessTokenExpiring());
    };

    const onUserUnloaded = () => {
      clearAuthenticatedUser();
      store.dispatch(userUnloaded());
    };

    const onUserSignedOut = () => {
      clearAuthenticatedUser();
      store.dispatch(userSignedOut());
    };

    userManager.events.addUserLoaded(onUserLoaded);
    userManager.events.addSilentRenewError(onSilentRenewError);
    if (!isLocal) {
      userManager.events.addAccessTokenExpired(onAccessTokenExpired);
      userManager.events.addAccessTokenExpiring(onAccessTokenExpiring);
    }
    userManager.events.addUserUnloaded(onUserUnloaded);
    userManager.events.addUserSignedOut(onUserSignedOut);

    return () => {
      userManager.events.removeUserLoaded(onUserLoaded);
      userManager.events.removeSilentRenewError(onSilentRenewError);
      if (!isLocal) {
        userManager.events.removeAccessTokenExpired(onAccessTokenExpired);
        userManager.events.removeAccessTokenExpiring(onAccessTokenExpiring);
      }
      userManager.events.removeUserUnloaded(onUserUnloaded);
      userManager.events.removeUserSignedOut(onUserSignedOut);
    };
  }, [userManager, store]);

  return React.Children.only(children);
}
